import React from "react";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import styled from "styled-components";

const LOGO_MAX_HEIGHT = 72;

const ImageGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-column-gap: 20px;
  grid-row-gap: 20px;
  align-items: center;
  max-width: 785px;
  margin: 0 auto;

  & .grid-item {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: ${LOGO_MAX_HEIGHT}px;

    & a {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;

      @media (hover: hover) and (pointer: fine) {
        transition: 250ms opacity ease;

        &:hover {
          opacity: 0.8;
        }
      }
    }

    & .gatsby-image-wrapper {
      width: 100% !important;
      height: ${LOGO_MAX_HEIGHT}px !important;
      max-width: 100%;

      img {
        object-fit: contain !important;
        object-position: center !important;
      }
    }
  }

  @media (max-width: 1280px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
    grid-column-gap: 10px;
    grid-row-gap: 10px;
  }
`;

export const ImageGrid = ({ items, id }) => {
  // GraphQL queries alias `image` as `gridImage` to avoid field conflicts.
  // Prismic preview replaces the document with unaliased field names, so
  // fall back to `image` when `gridImage` is missing.
  const gridItems = (items || [])
    .map(item => ({
      ...item,
      gridImage: item.gridImage || item.image,
    }))
    .filter(item => item.gridImage?.gatsbyImageData != null)
    .map((item, index) => {
      const image = (
        <GatsbyImage
          image={item.gridImage.gatsbyImageData}
          alt={item.gridImage.alt || ``}
          objectFit="contain"
          objectPosition="center"
          style={{ width: `100%`, height: `${LOGO_MAX_HEIGHT}px` }}
          imgStyle={{ objectFit: `contain`, objectPosition: `center` }}
        />
      );

      const hasLink = item.link?.url != null && item.link.url !== ``;
      const isDocument = item.link?.link_type === `Document`;
      const isMedia = item.link?.link_type === `Media`;
      const opensInNewTab = item.link?.target === `_blank`;

      let content = image;

      if (hasLink) {
        if (isDocument) {
          content = <Link to={item.link.url}>{image}</Link>;
        } else {
          content = (
            <a
              href={item.link.url}
              download={isMedia ? true : undefined}
              target={opensInNewTab ? `_blank` : undefined}
              rel={opensInNewTab ? `noopener noreferrer` : undefined}
            >
              {image}
            </a>
          );
        }
      }

      return (
        <div className="grid-item" key={`image_grid_item_${index}_${id}`}>
          {content}
        </div>
      );
    });

  return <ImageGridContainer>{gridItems}</ImageGridContainer>;
};
